import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { AdminStory, AdminStoryMedia } from '@fois-utsavvoice/shared';
import { ApiError } from '../api/client.js';
import {
  approveStory,
  approveStoryMedia,
  archiveStory,
  beginStoryReview,
  getAdminStory,
  getAdminStoryMediaDownloadUrl,
  getAdminStoryMediaViewUrl,
  listAdminStoryMedia,
  rejectStory,
  rejectStoryMedia,
  updateAdminStory,
} from '../api/stories.js';
import { useAuth } from '../auth/AuthContext.js';
import { AdminNav } from '../components/AdminNav.js';
import { PageShell } from '../components/PageShell.js';

export function AdminStoryDetailPage() {
  const { storyId } = useParams<{ storyId: string }>();
  const auth = useAuth();
  const [story, setStory] = useState<AdminStory | undefined>();
  const [notes, setNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [media, setMedia] = useState<AdminStoryMedia[]>([]);
  const [mediaReject, setMediaReject] = useState<Record<string, string>>({});
  const [previews, setPreviews] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!auth.tokens || !storyId) {
      return;
    }
    const token = auth.tokens.idToken;
    Promise.all([getAdminStory(token, storyId), listAdminStoryMedia(token, storyId)])
      .then(([next, gallery]) => {
        setStory(next);
        setNotes(next.adminNotes ?? '');
        setMedia(gallery.items);
      })
      .catch(() => setError('The story could not be loaded.'));
  }, [auth.tokens, storyId]);

  function accessToken(): string | undefined {
    return auth.tokens?.idToken;
  }

  async function run(action: () => Promise<AdminStory>): Promise<void> {
    setBusy(true);
    setError(undefined);
    try {
      const next = await action();
      setStory(next);
      setNotes(next.adminNotes ?? '');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'The story could not be updated.');
    } finally {
      setBusy(false);
    }
  }

  async function onSaveNotes(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!auth.tokens || !storyId) {
      return;
    }
    const token = accessToken();
    if (!token) {
      return;
    }
    await run(() => updateAdminStory(token, storyId, { adminNotes: notes }));
  }

  const canReview = story?.status === 'SUBMITTED';
  const canDecide = story?.status === 'SUBMITTED' || story?.status === 'IN_REVIEW';
  const canArchive =
    story?.status === 'APPROVED_PRIVATE' ||
    story?.status === 'APPROVED_FOR_PUBLICATION' ||
    story?.status === 'REJECTED';
  const canPublish = story?.consentLevel !== 'PRIVATE';

  return (
    <PageShell>
      <AdminNav />
      <section className="card mx-auto max-w-3xl">
        <p>
          <Link className="font-semibold text-crimson underline" to="/admin/stories">
            Back to stories
          </Link>
        </p>
        <h1 className="mt-4 heading-xl text-crimson">Story review</h1>
        {error ? (
          <p className="mt-3 text-sm font-semibold text-crimson" role="alert">
            {error}
          </p>
        ) : null}
        {story ? (
          <>
            <dl className="mt-6 grid gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-semibold text-charcoal/70">Product</dt>
                <dd>{story.productName}</dd>
              </div>
              <div>
                <dt className="text-sm font-semibold text-charcoal/70">Order</dt>
                <dd>{story.orderReference ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-sm font-semibold text-charcoal/70">Status</dt>
                <dd>{story.status}</dd>
              </div>
              <div>
                <dt className="text-sm font-semibold text-charcoal/70">Consent</dt>
                <dd>{story.consentLevel ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-sm font-semibold text-charcoal/70">Display name</dt>
                <dd>{story.displayName ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-sm font-semibold text-charcoal/70">Location</dt>
                <dd>{story.locationText ?? '—'}</dd>
              </div>
            </dl>
            <div className="mt-6 space-y-4">
              <AdminMediaGallery
                title="Photos"
                empty="No photos uploaded."
                items={media.filter((item) => item.mediaType === 'PHOTO')}
                previews={previews}
                mediaReject={mediaReject}
                canDecide={canDecide}
                busy={busy}
                storyId={storyId}
                accessToken={accessToken()}
                onPreview={(mediaId, url) =>
                  setPreviews((current) => ({ ...current, [mediaId]: url }))
                }
                onError={setError}
                onBusy={setBusy}
                onMediaChange={setMedia}
                onRejectChange={setMediaReject}
              />
              <AdminMediaGallery
                title="Videos"
                empty="No videos uploaded."
                items={media.filter((item) => item.mediaType === 'VIDEO')}
                previews={previews}
                mediaReject={mediaReject}
                canDecide={canDecide}
                busy={busy}
                storyId={storyId}
                accessToken={accessToken()}
                onPreview={(mediaId, url) =>
                  setPreviews((current) => ({ ...current, [mediaId]: url }))
                }
                onError={setError}
                onBusy={setBusy}
                onMediaChange={setMedia}
                onRejectChange={setMediaReject}
              />
              <div>
                <h2 className="heading-lg text-crimson">Testimonial</h2>
                <p className="mt-2 whitespace-pre-wrap text-charcoal">
                  {story.testimonial ?? 'No testimonial yet.'}
                </p>
              </div>
              <div>
                <h2 className="heading-lg text-crimson">Celebration meaning</h2>
                <p className="mt-2 whitespace-pre-wrap text-charcoal">
                  {story.celebrationMeaning ?? '—'}
                </p>
              </div>
            </div>
            <form className="mt-6 space-y-3" onSubmit={(event) => void onSaveNotes(event)}>
              <label className="field-label" htmlFor="adminNotes">
                Administrator notes
              </label>
              <textarea
                id="adminNotes"
                className="field-input min-h-24"
                maxLength={2000}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />
              <button className="btn-secondary" type="submit" disabled={busy}>
                Save notes
              </button>
            </form>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              {canReview ? (
                <button
                  className="btn-secondary"
                  type="button"
                  disabled={busy || !auth.tokens || !storyId}
                  onClick={() => {
                    const token = accessToken();
                    if (token && storyId) {
                      void run(() => beginStoryReview(token, storyId));
                    }
                  }}
                >
                  Begin review
                </button>
              ) : null}
              {canDecide ? (
                <>
                  <button
                    className="btn-secondary"
                    type="button"
                    disabled={busy || !auth.tokens || !storyId}
                    onClick={() => {
                      const token = accessToken();
                      if (token && storyId) {
                        void run(() => approveStory(token, storyId, { forPublication: false }));
                      }
                    }}
                  >
                    Approve privately
                  </button>
                  <button
                    className="btn-primary"
                    type="button"
                    disabled={busy || !canPublish || !auth.tokens || !storyId}
                    onClick={() => {
                      const token = accessToken();
                      if (token && storyId) {
                        void run(() => approveStory(token, storyId, { forPublication: true }));
                      }
                    }}
                  >
                    Approve for publication
                  </button>
                </>
              ) : null}
              {canArchive ? (
                <button
                  className="btn-secondary"
                  type="button"
                  disabled={busy || !auth.tokens || !storyId}
                  onClick={() => {
                    const token = accessToken();
                    if (token && storyId) {
                      void run(() => archiveStory(token, storyId));
                    }
                  }}
                >
                  Archive
                </button>
              ) : null}
            </div>
            {!canPublish && canDecide ? (
              <p className="mt-3 text-sm text-charcoal/80">
                Private consent cannot be approved for publication.
              </p>
            ) : null}
            {canDecide ? (
              <form
                className="mt-6 space-y-3"
                onSubmit={(event) => {
                  event.preventDefault();
                  const token = accessToken();
                  if (!token || !storyId || !rejectReason.trim()) {
                    return;
                  }
                  void run(() => rejectStory(token, storyId, { reason: rejectReason.trim() }));
                }}
              >
                <label className="field-label" htmlFor="rejectReason">
                  Rejection reason
                </label>
                <input
                  id="rejectReason"
                  className="field-input"
                  required
                  maxLength={500}
                  value={rejectReason}
                  onChange={(event) => setRejectReason(event.target.value)}
                />
                <button className="btn-secondary" type="submit" disabled={busy}>
                  Reject story
                </button>
              </form>
            ) : null}
          </>
        ) : (
          <p className="mt-4" role="status">
            Loading story…
          </p>
        )}
      </section>
    </PageShell>
  );
}

function AdminMediaGallery({
  title,
  empty,
  items,
  previews,
  mediaReject,
  canDecide,
  busy,
  storyId,
  accessToken,
  onPreview,
  onError,
  onBusy,
  onMediaChange,
  onRejectChange,
}: {
  title: string;
  empty: string;
  items: AdminStoryMedia[];
  previews: Record<string, string>;
  mediaReject: Record<string, string>;
  canDecide: boolean;
  busy: boolean;
  storyId: string | undefined;
  accessToken: string | undefined;
  onPreview: (mediaId: string, url: string) => void;
  onError: (message: string) => void;
  onBusy: (busy: boolean) => void;
  onMediaChange: (updater: (current: AdminStoryMedia[]) => AdminStoryMedia[]) => void;
  onRejectChange: (updater: (current: Record<string, string>) => Record<string, string>) => void;
}) {
  const visible = items.filter((item) => item.status !== 'REMOVED_BY_CUSTOMER' && item.status !== 'DELETED');
  const noun = title.toLowerCase().slice(0, -1);

  return (
    <div>
      <h2 className="heading-lg text-crimson">{title}</h2>
      {visible.length ? (
        <ul className="mt-3 grid gap-4 sm:grid-cols-2">
          {visible.map((item) => (
            <li key={item.mediaId} className="rounded-lg border border-kraft/40 p-3">
              {previews[item.mediaId] ? (
                item.mediaType === 'VIDEO' ? (
                  <video
                    className="mb-3 max-h-48 w-full rounded bg-charcoal"
                    src={previews[item.mediaId]}
                    controls
                    playsInline
                    preload="metadata"
                    onError={() =>
                      onError(
                        'This video could not be played in this browser. Use Download to open the original iPhone clip.',
                      )
                    }
                  />
                ) : (
                  <img
                    className="mb-3 max-h-48 w-full rounded object-cover"
                    src={previews[item.mediaId]}
                    alt="Customer story photo"
                  />
                )
              ) : null}
              {!previews[item.mediaId] || item.mediaType === 'VIDEO' ? (
                <div className="mb-3 flex flex-wrap gap-2">
                  {previews[item.mediaId] ? null : (
                    <button
                      className="btn-secondary"
                      type="button"
                      onClick={() => {
                        if (!accessToken || !storyId) {
                          return;
                        }
                        void getAdminStoryMediaViewUrl(accessToken, storyId, item.mediaId)
                          .then((result) => onPreview(item.mediaId, result.url))
                          .catch(() => onError(`The ${noun} could not be opened.`));
                      }}
                    >
                      {item.mediaType === 'VIDEO' ? 'View video' : 'View photo'}
                    </button>
                  )}
                  {item.mediaType === 'VIDEO' ? (
                    <button
                      className="btn-secondary"
                      type="button"
                      onClick={() => {
                        if (!accessToken || !storyId) {
                          return;
                        }
                        void getAdminStoryMediaDownloadUrl(accessToken, storyId, item.mediaId)
                          .then((result) => {
                            window.open(result.url, '_blank', 'noopener,noreferrer');
                          })
                          .catch(() => onError('The video could not be downloaded.'));
                      }}
                    >
                      Download
                    </button>
                  ) : null}
                </div>
              ) : null}
              <p className="text-sm text-charcoal/80">{item.status}</p>
              {canDecide && (item.status === 'READY' || item.status === 'REJECTED_BY_ADMIN') ? (
                <button
                  className="btn-secondary mt-2"
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    if (!accessToken || !storyId) {
                      return;
                    }
                    onBusy(true);
                    void approveStoryMedia(accessToken, storyId, item.mediaId)
                      .then((next) =>
                        onMediaChange((current) =>
                          current.map((entry) => (entry.mediaId === next.mediaId ? next : entry)),
                        ),
                      )
                      .catch((caught) =>
                        onError(
                          caught instanceof ApiError
                            ? caught.message
                            : `The ${noun} could not be approved.`,
                        ),
                      )
                      .finally(() => onBusy(false));
                  }}
                >
                  {item.mediaType === 'VIDEO' ? 'Approve video' : 'Approve photo'}
                </button>
              ) : null}
              {canDecide && (item.status === 'READY' || item.status === 'APPROVED') ? (
                <form
                  className="mt-3 space-y-2"
                  onSubmit={(event) => {
                    event.preventDefault();
                    const reason = mediaReject[item.mediaId]?.trim();
                    if (!accessToken || !storyId || !reason) {
                      return;
                    }
                    onBusy(true);
                    void rejectStoryMedia(accessToken, storyId, item.mediaId, { reason })
                      .then((next) =>
                        onMediaChange((current) =>
                          current.map((entry) => (entry.mediaId === next.mediaId ? next : entry)),
                        ),
                      )
                      .catch((caught) =>
                        onError(
                          caught instanceof ApiError
                            ? caught.message
                            : `The ${noun} could not be rejected.`,
                        ),
                      )
                      .finally(() => onBusy(false));
                  }}
                >
                  <label className="field-label" htmlFor={`media-reject-${item.mediaId}`}>
                    {item.mediaType === 'VIDEO' ? 'Video rejection reason' : 'Photo rejection reason'}
                  </label>
                  <input
                    id={`media-reject-${item.mediaId}`}
                    className="field-input"
                    required
                    maxLength={500}
                    value={mediaReject[item.mediaId] ?? ''}
                    onChange={(event) =>
                      onRejectChange((current) => ({
                        ...current,
                        [item.mediaId]: event.target.value,
                      }))
                    }
                  />
                  <button className="btn-secondary" type="submit" disabled={busy}>
                    {item.mediaType === 'VIDEO' ? 'Reject video' : 'Reject photo'}
                  </button>
                </form>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-charcoal">{empty}</p>
      )}
    </div>
  );
}
